import { useState, useEffect, useCallback } from "react";
import * as ceoService from "../services/ceoDataService";

/**
 * Hook para el CEO Dashboard v2 — carga los JSONs consolidados en paralelo.
 *
 * Carga inicial: ~20 KB (JSONs pequeños).
 * Lazy loaders: newMembers (56 KB), allMeasures (29 KB), employees (73 KB).
 */
export default function useCEOData() {
  const [population, setPopulation] = useState(null);
  const [cost, setCost] = useState(null);
  const [quality, setQuality] = useState(null);
  const [centerPerformance, setCenterPerformance] = useState(null);
  const [trends, setTrends] = useState(null);
  const [coding, setCoding] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [performance, setPerformance] = useState(null);

  const [loading, setLoading] = useState({
    population: true,
    cost: true,
    quality: true,
    centerPerformance: true,
    trends: true,
    coding: true,
    engagement: true,
    performance: true,
  });

  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const results = await Promise.allSettled([
        ceoService.getPopulationOverview(),          // 0
        ceoService.getCostSummary(),                 // 1
        ceoService.getCostMonthly(),                 // 2
        ceoService.getCostByLocation(),              // 3
        ceoService.getCostTopPCPs(),                 // 4
        ceoService.getCostTopFacilities(),           // 5
        ceoService.getQualitySummary(),              // 6
        ceoService.getTopMeasures(),                 // 7
        ceoService.getCenterPerformance(),           // 8
        ceoService.getTrends(),                      // 9
        ceoService.getCodingSummary(),               // 10
        ceoService.getCodingTopHCCs(),               // 11
        ceoService.getCodingRecaptureByPCP(),        // 12
        ceoService.getEngagement(),                  // 13
        ceoService.getPerformanceSummary(),          // 14
      ]);

      if (cancelled) return;

      const val = (i) => results[i].status === "fulfilled" ? results[i].value : null;

      setPopulation(val(0));

      setCost({
        summary: val(1),
        monthly: val(2),
        byLocation: val(3),
        topPCPs: val(4),
        topFacilities: val(5),
      });

      setQuality({
        summary: val(6),
        topMeasures: val(7),
      });

      setCenterPerformance(val(8));
      setTrends(val(9));

      setCoding({
        summary: val(10),
        topHCCs: val(11),
        recaptureByPCP: val(12),
      });

      setEngagement(val(13));
      setPerformance({ summary: val(14) });

      const firstError = results.find((r) => r.status === "rejected");
      if (firstError) setError(firstError.reason);

      setLoading({
        population: false,
        cost: false,
        quality: false,
        centerPerformance: false,
        trends: false,
        coding: false,
        engagement: false,
        performance: false,
      });
    }

    load();
    return () => { cancelled = true; };
  }, []);

  // Lazy loaders — llamar bajo demanda
  const loadNewMembers = useCallback(() => ceoService.getNewMembers(), []);
  const loadAllMeasures = useCallback(() => ceoService.getAllMeasures(), []);
  const loadEmployees = useCallback(() => ceoService.getEmployees(), []);
  const loadClinicalPerformance = useCallback(() => ceoService.getClinicalPerformance(), []);

  return {
    population,
    cost,
    quality,
    centerPerformance,
    trends,
    coding,
    engagement,
    performance,
    loading,
    error,
    loadNewMembers,
    loadAllMeasures,
    loadEmployees,
    loadClinicalPerformance,
  };
}
